import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { apiEndpoints, publicFetch } from "../config/api";

export default function Blog() {
  const [blogPosts, setBlogPosts] = useState([]);

  const fetchBlogs = async () => {
    try {
      const response = await publicFetch(apiEndpoints.blogs.base);

      if (response.ok) {
        const data = await response.json();
        setBlogPosts(data);
      } else {
        console.error("Error fetching blogs:", response.status);
      }
    } catch (err) {
      console.error("Error fetching blogs:", err);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Listen for blog updates from admin panel
  useEffect(() => {
    const handleBlogUpdate = () => {
      fetchBlogs();
    };

    // Listen for custom blog update events
    window.addEventListener("blogUpdated", handleBlogUpdate);

    // Also listen for storage changes (in case admin is in different tab)
    const handleStorageChange = (e) => {
      if (e.key === "lastBlogUpdate") {
        fetchBlogs();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("blogUpdated", handleBlogUpdate);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const categories = [
    { name: "All", count: blogPosts.length },
    {
      name: "Travel Tips",
      count: blogPosts.filter((post) => post.category === "Travel Tips").length,
    },
    {
      name: "Wildlife",
      count: blogPosts.filter((post) => post.category === "Wildlife").length,
    },
    {
      name: "Conservation",
      count: blogPosts.filter((post) => post.category === "Conservation")
        .length,
    },
    {
      name: "Photography",
      count: blogPosts.filter((post) => post.category === "Photography").length,
    },
    {
      name: "Family Travel",
      count: blogPosts.filter((post) => post.category === "Family Travel")
        .length,
    },
    {
      name: "Birdwatching",
      count: blogPosts.filter((post) => post.category === "Birdwatching")
        .length,
    },
  ];

  const [activeCategory, setActiveCategory] = useState("All");

  const filteredPosts =
    activeCategory === "All"
      ? blogPosts
      : blogPosts.filter((post) => post.category === activeCategory);

  return (
    <div className="mdc-blog-page bg-[#1a1f28]">
      <section className="mdc-blog-hero">
        <div className="mdc-blog-hero-overlay" />
        <div className="mdc-blog-hero-content">
          <span className="mdc-hero-kicker">Safari Blog & Tips</span>
          <h1 className="mdc-blog-title">
            Stories, sightings, and strategies for your next Sri Lankan safari.
          </h1>
          <p className="mdc-blog-subtitle">
            Expert advice from our guiding team, real-time wildlife intel, and
            conservation insights to help you travel responsibly.
          </p>
          <div className="mdc-hero-actions">
            <button onClick={fetchBlogs} className="mdc-secondary-button">
              Refresh feed
            </button>
            <div className="mdc-stat-badges">
              <div className="mdc-stat-badge">
                <span className="mdc-dot" aria-hidden="true"></span>
                <div>
                  <p className="mdc-stat-number">
                    {blogPosts.length > 99 ? "99+" : blogPosts.length}
                  </p>
                  <p className="mdc-stat-label">Stories published</p>
                </div>
              </div>
              <div className="mdc-stat-badge">
                <span className="mdc-dot" aria-hidden="true"></span>
                <div>
                  <p className="mdc-stat-number">{categories.length - 1}</p>
                  <p className="mdc-stat-label">Topics explored</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mdc-section">
        <div className="mdc-section-intro text-center">
          <h2 className="mdc-section-title">Browse by interest</h2>
          <p className="mdc-section-description">
            Zero in on the safari intel that matters most for your upcoming
            adventure.
          </p>
        </div>
        <div className="mdc-pill-bar">
          {categories.map((category) => {
            const isActive = activeCategory === category.name;
            return (
              <button
                key={category.name}
                onClick={() => setActiveCategory(category.name)}
                className={`mdc-pill ${isActive ? "mdc-pill--active" : ""}`}
                type="button"
              >
                <span>{category.name}</span>
                <span
                  className={`mdc-pill-count ${
                    isActive ? "mdc-pill-count--active" : ""
                  }`}
                >
                  {category.count}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mdc-section">
        <div className="mdc-blog-grid">
          {filteredPosts.map((post) => (
            <article key={post.id} className="mdc-blog-card">
              <Link to={`/blog/${post.id}`} className="mdc-blog-card-link">
                <div className="mdc-blog-card-media">
                  <img
                    className="mdc-blog-card-image"
                    src={post.image}
                    alt={post.title}
                  />
                  <span className="mdc-blog-card-category">{post.category}</span>
                </div>
                <div className="mdc-blog-card-body">
                  <h3 className="mdc-blog-card-title">{post.title}</h3>
                  <p className="mdc-blog-card-excerpt">{post.excerpt}</p>
                </div>
              </Link>

              <div className="mdc-blog-card-footer">
                <div className="mdc-author-avatar">
                  <span>YL</span>
                </div>
                <div>
                  <p className="mdc-author-name">Yala Safari Team</p>
                  <div className="mdc-author-meta">
                    <time dateTime={post.date}>{post.date}</time>
                    <span className="mdc-meta-dot" aria-hidden="true"></span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="mdc-empty-state">
            <h3 className="mdc-empty-title">No posts found in this category</h3>
            <p className="mdc-empty-text">
              We don&apos;t have any stories filed under {activeCategory} yet.
              Explore all articles while we craft new content.
            </p>
            <button
              onClick={() => setActiveCategory("All")}
              className="mdc-outline-button"
              type="button"
            >
              View all posts
            </button>
          </div>
        )}
      </section>

      <section className="mdc-newsletter">
        <h3 className="mdc-newsletter-title">Want more safari tips?</h3>
        <p className="mdc-newsletter-text">
          Subscribe for monthly wildlife round-ups, conservation news, and
          exclusive guest offers curated by our guiding team.
        </p>
        <form className="mdc-newsletter-form">
          <label htmlFor="email-address" className="sr-only">
            Email address
          </label>
          <input
            id="email-address"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="mdc-input"
            placeholder="Enter your email"
          />
          <button type="submit" className="mdc-primary-button">
            Subscribe
          </button>
        </form>
      </section>
    </div>
  );
}
